import { cn, ToastProps } from '@heroui/react';

/**
 * Wspólne style toastów (panel-app), w stylu hotelspot-audit: ostre krawędzie, ramka.
 *
 * HeroUI domyślnie renderuje toast jako kartę 356px z paddingiem 12px i chowa
 * przycisk zamykania (`opacity-0 group-hover:opacity-100`). Tutaj zmniejszamy
 * wysokość/typografię oraz na stałe pokazujemy „X", żeby toast dało się zamknąć
 * bez czekania na timeout (i bez zgadywania, że trzeba najechać myszką).
 */

export type ToastTone = 'danger' | 'success' | 'warning' | 'primary' | 'default';

/** Krótkie czasy życia toastów — komunikat ma nie blokować widoku. */
export const TOAST_TIMEOUT = {
  /** Potwierdzenia („zapisano", „skopiowano"). */
  short: 1800,
  /** Ostrzeżenia i błędy — trochę więcej czasu na przeczytanie. */
  default: 2500,
  /** Komunikaty wymagające reakcji (wygasła sesja, limit AI). */
  long: 4000,
} as const;

type ToastClassNames = NonNullable<ToastProps['classNames']>;

const resetStyles = '!border-none !shadow-none !outline-none';

const toastBase = cn([
  '!bg-white',
  'dark:!bg-zinc-900',
  '!bg-none',
  '!backdrop-blur-none',
  '!border-none',
  '!shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]',
  'dark:!shadow-[0_4px_16px_rgba(0,0,0,0.32),0_1px_4px_rgba(0,0,0,0.16)]',
  '!outline-none',
  '!p-0',
  '!my-1',
  '!min-h-0',
  // Styl hotelspot-audit: ostre krawędzie, większa karta, cienka ramka zamiast miękkiego cienia.
  '!rounded-none',
  '!border',
  '!border-solid',
  '!border-border-default',
  'dark:!border-zinc-700',
  '!relative',
  '!w-full',
  'sm:!w-[400px]',
]);

// Wiersz ikona + tekst — domyślny gap-x-4 jest za duży dla kompaktowej wersji.
const toastContent = cn([
  resetStyles,
  '!p-0',
  '!m-0',
  '!gap-x-3',
  '!items-start',
  '[&_div]:!border-none',
  '[&_div]:!shadow-none',
  '[&_div]:!outline-none',
]);

// Kolumna z tytułem i opisem — to ona nadaje toastowi padding (base ma !p-0).
// UWAGA: bez własnego z-index i bez tła — inaczej biała kolumna zamalowuje
// przycisk zamykania (jest wyżej w DOM, ale niżej w kontekście stosu).
const toastWrapper = cn([
  '!rounded-none',
  '!bg-transparent',
  '!py-3.5',
  '!pl-0',
  '!pr-9',
  '!gap-y-0.5',
  '!min-h-0',
  '!border-none',
  '!outline-none',
  '!relative',
]);

const toastTitle = cn([
  '!font-medium',
  '!text-[15px]',
  '!leading-[1.3]',
  '!tracking-[-0.01em]',
  '!me-0',
  resetStyles,
]);

const toastDescription = cn([
  '!font-normal',
  '!text-[13px]',
  '!leading-[1.45]',
  '!me-0',
  '!mt-0.5',
  '!text-text-secondary',
  'dark:!text-default-400',
  resetStyles,
]);

// „X" na stałe widoczny (HeroUI domyślnie odsłania go dopiero na hover).
const toastCloseButton = cn([
  resetStyles,
  '!absolute',
  '!z-10',
  '!top-2',
  '!right-2',
  '!w-6',
  '!h-6',
  '!min-w-6',
  '!min-h-6',
  '!p-0',
  '!opacity-100',
  '!pointer-events-auto',
  '!bg-transparent',
  'hover:!bg-default-100',
  'dark:hover:!bg-zinc-800',
  '!rounded-none',
  '!text-default-400',
  'hover:!text-default-600',
  'dark:!text-default-500',
  'dark:hover:!text-default-300',
]);

const toastCloseIcon = cn([
  resetStyles,
  '!w-3.5',
  '!h-3.5',
  '!p-0',
  '!bg-transparent',
  '!rounded-none',
]);

// Ikona to samo `<svg>` (HeroUI klonuje element z klasami slotu), więc padding
// zmniejsza rysowany glif wewnątrz kolorowego kółka.
const iconBase = [
  '!w-7',
  '!h-7',
  '!min-w-7',
  '!min-h-7',
  '!ml-3.5',
  '!mt-3',
  '!p-1.5',
  '!rounded-none',
  '!shrink-0',
  '!fill-none',
  resetStyles,
];

const toneIcon: Record<ToastTone, string> = {
  danger: cn([...iconBase, '!bg-danger-50', 'dark:!bg-danger-900/20', '!text-danger-500']),
  success: cn([...iconBase, '!bg-success-50', 'dark:!bg-success-900/20', '!text-success-500']),
  warning: cn([...iconBase, '!bg-warning-50', 'dark:!bg-warning-900/20', '!text-warning-500']),
  primary: cn([...iconBase, '!bg-primary-50', 'dark:!bg-primary-900/20', '!text-primary-500']),
  default: cn([...iconBase, '!bg-default-100', 'dark:!bg-zinc-800', '!text-default-500']),
};

// Tytuł zawsze atramentowy; o typie komunikatu mówi kolorowa ikona, nie kolorowy tekst.
const toneTitle: Record<ToastTone, string> = {
  danger: '!text-text-primary dark:!text-zinc-100',
  success: '!text-text-primary dark:!text-zinc-100',
  warning: '!text-text-primary dark:!text-zinc-100',
  primary: '!text-text-primary dark:!text-zinc-100',
  default: '!text-text-primary dark:!text-zinc-100',
};

export const getToastClassNames = (tone: ToastTone = 'default'): ToastClassNames => ({
  base: toastBase,
  wrapper: toastWrapper,
  content: toastContent,
  title: cn([toastTitle, toneTitle[tone]]),
  description: toastDescription,
  icon: toneIcon[tone],
  closeButton: toastCloseButton,
  closeIcon: toastCloseIcon,
});

/**
 * Wspólne propsy `addToast` — kompaktowy wygląd + zawsze widoczny „X".
 * `color` zostaje `default` (tło toasta jest białe), a `severity` decyduje tylko
 * o domyślnej ikonie, gdy wywołanie nie poda własnej.
 */
export const compactToastProps = (tone: ToastTone = 'default'): Partial<ToastProps> => ({
  color: 'default',
  severity: tone,
  variant: 'bordered',
  radius: 'none',
  hideCloseButton: false,
  classNames: getToastClassNames(tone),
});
